/**
 * This module is to display the popup to guide the user trough the site the first time he
 * lands on it. It will be device, page, sport, and logged status based.
 *
 *
 * @module app/components/FirstTimeUserJourney
 */
import _ from 'lodash';
import DeviceDetector from './DeviceDetector';
import FootballPopup from '../views/templates/FirstTimeUserJourney/FootballPopup';
import homePagePopup from '../views/templates/FirstTimeUserJourney/homePagePopup';
import horseRacingPopup from '../views/templates/FirstTimeUserJourney/horseRacingPopup';
import jQuery from 'jquery';
import Login from './Login';
import MessageBusTopics from '../../common/MessageBusTopics';
import StorageProvider from './StorageProvider';
import Translator from './Translator';

/**
 * This is the value to be read/written from storage provider
 *
 * @type {Object}
 *
 * @private
 */
var platforms = {
    native: 'NATIVE',
    web: 'WEB',
};

/**
 * This variable will store the login status once the Login.js has published it
 *
 * @type {boolean|undefined}
 *
 * @private
 */
var isLoggedIn;

/**
 * This is the value to be read/written from storage provider
 *
 * @type {String}
 *
 * @private
 */
var storageKey = 'firstTimeJourney';

/**
 * This object describes the rules to display the popup based on the page
 * For some pages there could be conditions about the sports.
 *
 * @type {Object}
 *
 * @private
 */
var pages = {};

/**
 * This method initilizes the configuration per page
 *
 * @returns {void}
 *
 * @private
 */
var initPages = function initPages() {

    pages = {
        HOME: {
            page: 'HOME',
            displayLogged: false,
            displayNotLogged: true,
            platforms: [platforms.web],
            sports: [], // empty means not sport based
            template: homePagePopup,
        },
        MEETINGS: {
            page: 'MEETINGS',
            displayLogged: true,
            displayNotLogged: true,
            platforms: [platforms.web, platforms.native],
            sports: ['horse-racing'], // empty means all
            template: horseRacingPopup,
        },
        HIGHLIGHTS: {
            page: 'HIGHLIGHTS',
            displayLogged: true,
            displayNotLogged: true,
            platforms: [platforms.web],
            sports: ['football'], // just football
            template: FootballPopup,
        },
    };
};

/**
 * This method tells if this is the first time based on localstoragedata
 *
 * @param   {String}    page   page cookie name
 *
 * @returns {void}
 *
 * @private
 */
var isUserFirstTime = function isUserFirstTime(page) {

    var cookie = JSON.parse(StorageProvider.get(storageKey));

    return ( cookie.hasOwnProperty(page) && cookie[page] === 'true');
};

/**
 * Checks if the current login status is elegible to display the popup based on the page
 * configuration
 *
 * @param   {Booelan}    loggedIn    Login user status
 * @param   {Object}     pageObject  Object containing the config for the given page
 *
 * @returns {Booelan}
 */
var loginStatusElegible = function loginStatusElegible(loggedIn, pageObject) {

    return (loggedIn) ? pageObject.displayLogged : pageObject.displayNotLogged;
};

/**
 * Returns the current platform
 *
 * @returns {String}
 *
 * @private
 */
var getCurrentPlatform = function getCurrentPlatform() {

    return (DeviceDetector.isNative()) ? platforms.native : platforms.web;
};

/**
 * Checks if the current device is a native app
 *
 * @param   {Object}    pageObject  Object containing the config for the given page
 *
 * @returns {Booelan}
 */
var isElegiblePlatform = function isElegiblePlatform(pageObject) {

    return _.includes(pageObject.platforms, getCurrentPlatform());
};

/**
 * Checks if the current sport is elegible to display the popup based on the page
 * configuration
 *
 * @param   {String}    sport       Login user status
 * @param   {Object}    pageObject  Object containing the config for the given page
 *
 * @returns {Booelan}
 */
var sportIsElegible = function sportIsElegible(sport, pageObject) {

    return ( pageObject.sports.length === 0 || _.includes(pageObject.sports, sport) );
};

/**
 * Renders and display the popup
 *
 * @param   {String}    sport       Login user status
 * @param   {Object}    pageObject  Object containing the config for the given page
 *
 * @returns {Booelan}
 *
 * @private
 */
var display = function display(sport, pageObject) {

    jQuery.magnificPopup.open({
        items: {
            src: _.template(pageObject.template)({ translator: Translator }),
            type: 'inline',
        },
        callbacks: {
            open: function () {

                updateStorage(pageObject.page);

                jQuery('#popupCloseButton, #popupMainButton')
                    .off('click.FTUJ')
                    .on('click.FTUJ', closePopup);
            },
        },
        showCloseBtn: false,
    });


};

/**
 * Function that closes the popup
 *
 * @returns {void}
 *
 * @private
 */
var closePopup = function closePopup() {

    jQuery.magnificPopup.close();
};

/**
 * Updates the storage so we consider that the user has seen the popup
 *
 * @param   {String}    page    page cookie name
 *
 * @returns {void}
 *
 * @private
 */
var updateStorage = function updateStorage(page) {

    var cookie = JSON.parse(StorageProvider.get(storageKey));

    cookie[page] = 'false';

    StorageProvider.set(storageKey, JSON.stringify(cookie));
};

/**
 * Determinates if device is mobile or tablet
 *
 * @returns {Boolean}
 *
 * @private
 */
var isElegibleDevice = function isElegibleDevice() {

    return (DeviceDetector.isTablet() || DeviceDetector.isMobile());
};

/**
 * This function is to check if the popup is to be displayed based in many factors.
 * If elegible then the correct template is rendered and the popup is displayed.
 *
 * @param   {Object}    pageObject      Object containing the config for the given page
 * @param   {String}    sport           Login user status
 * @param   {Object}    LoginJsMessage  Object received from the messagebush publishment from Login.js
 *
 * @returns {void}
 *
 * @private
 */
var checkAndDisplay = function checkAndDisplay(pageObject, sport, LoginJsMessage) {

    if (isElegibleDevice() &&
        isUserFirstTime(pageObject.page) &&
        sportIsElegible(sport, pageObject) &&
        isElegiblePlatform(pageObject) &&
        loginStatusElegible(LoginJsMessage.isLoggedIn, pageObject)) {

        window.WH.messageBus.subscribe(MessageBusTopics.DEFERRED_LOAD_MAGNIFIC_POPUP, function () {
            display(sport, pageObject);
        });

    }
};

/**
 * Initialized the module, waits for login info if needed.
 *
 * @param   {Object}    pageObject  Object containing the config for the given page
 * @param   {String}    sport       Login user status
 *
 * @returns {void}
 *
 * @public
 */
var initFirstTimeUser = function init(pageObject, sport) {

    isLoggedIn = Login.isLoggedIn();

    if (isLoggedIn === undefined) {

        window.WH.messageBus.subscribe(MessageBusTopics.GOT_LOGIN_STATE, function (LoginJsMessage) {

            checkAndDisplay(pageObject, sport, LoginJsMessage);
        });

    } else {

        checkAndDisplay(pageObject, sport, { isLoggedIn: isLoggedIn });
    }
};

/**
 * Public API.
 *
 * @type {Object}
 *
 * @public
 */
var api = {

    /**
     * Initialized the module, waits for login info if needed.
     *
     * @param   {Object}    pageObject  Object containing the config for the given page
     * @param   {String}    sport       Login user status
     *
     * @returns {void}
     *
     * @public
     */
    init: function init(pageObject, sport) {

        if (window.WH.sportsbook.firstTimeUserJourney) {

            initFirstTimeUser(pageObject, sport);
        }
    },

    /**
     * Returns all the page configurations
     *
     * @returns {Object} page configurations to be used in other modules
     *
     * @public
     */
    getPages: function getPages() {

        return pages;
    },

};

initPages();

export default api;
